import React, { useEffect, useMemo, useState } from 'react';

import { GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';

import * as turf from '@turf/turf';
import L from 'leaflet';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'leaflet/dist/leaflet.css';

import dayjs from 'dayjs';

/*
|--------------------------------------------------------------------------
| PENTING
|--------------------------------------------------------------------------
|
| Pakai file HASIL BUILD:
|
| npm run map:wilayah
|
| JANGAN lagi memakai:
|
| kabupaten_tangerang_fc.json
|
*/

import kabupaten from '../assets/batas/kabupaten_tangerang_wilayah_fc.json';

import plumberImg from '../assets/images/maps/plumber.png';

import api from './Api';

/*
|--------------------------------------------------------------------------
| KONFIGURASI WILAYAH
|--------------------------------------------------------------------------
*/

const WILAYAH_CONFIG = {
  2: {
    name: 'Wilayah II',
    code: 'WII',
    color: '#69D3A5',
  },

  4: {
    name: 'Wilayah Serpong',
    code: 'WSRP',
    color: '#9FA8E8',
  },

  5: {
    name: 'Wilayah Teluknaga',
    code: 'WTN',
    color: '#F6B77A',
  },

  8: {
    name: 'Wilayah Zona C',
    code: 'WZC',
    color: '#F4DC7C',
  },

  11: {
    name: 'Wilayah Tigaraksa',
    code: 'WTGRS',
    color: '#7EC8F4',
  },

  13: {
    name: 'Wilayah GS & BSD',
    code: 'WGS&BSD',
    color: '#F3A6C0',
  },

  15: {
    name: 'Wilayah Citra Raya',
    code: 'WCR',
    color: '#80D8B5',
  },

  16: {
    name: 'Wilayah V',
    code: 'WV',
    color: '#B4A5E5',
  },
};

const DEFAULT_AREA_COLOR = '#CBD5E1';

/*
|--------------------------------------------------------------------------
| RESIZE MAP
|--------------------------------------------------------------------------
*/

function ResizeMap({ isFullscreen }) {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [isFullscreen, map]);

  return null;
}

/*
|--------------------------------------------------------------------------
| FLY KE PETUGAS
|--------------------------------------------------------------------------
*/

function FlyToPetugas({ user }) {
  const map = useMap();

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!Number.isFinite(user.lat) || !Number.isFinite(user.lng)) {
      return;
    }

    map.flyTo([user.lat, user.lng], 15, {
      duration: 1,
    });
  }, [user, map]);

  return null;
}

/*
|--------------------------------------------------------------------------
| ICON WORK ORDER
|--------------------------------------------------------------------------
*/

const createWorkOrderIcon = (status, jenis) => {
  let bgColor = '#0d6efd';

  if (status === 'in_progress') {
    bgColor = '#f59e0b';
  }

  if (status === 'pending') {
    bgColor = '#ef4444';
  }

  const label = jenis === 'teknis' ? 'T' : 'A';

  return L.divIcon({
    className: '',

    html: `
      <div
        style="
          width:32px;
          height:32px;

          background:${bgColor};

          border-radius:50%;

          display:flex;
          align-items:center;
          justify-content:center;

          border:2px solid #ffffff;

          box-shadow:
            0 2px 8px rgba(0,0,0,0.35);

          color:#ffffff;

          font-size:13px;
          font-weight:700;
        "
      >
        ${label}
      </div>
    `,

    iconSize: [32, 32],

    iconAnchor: [16, 16],

    popupAnchor: [0, -18],
  });
};

/*
|--------------------------------------------------------------------------
| ICON PETUGAS
|--------------------------------------------------------------------------
*/

const createPetugasIcon = (isActive) => {
  const imageFilter = isActive ? 'none' : 'grayscale(100%) opacity(0.60)';

  const statusColor = isActive ? '#22c55e' : '#94a3b8';

  return L.divIcon({
    className: '',

    html: `
      <div
        style="
          position:relative;

          width:48px;
          height:52px;
        "
      >
        <img
          src="${plumberImg}"
          style="
            width:46px;
            height:46px;

            object-fit:contain;

            filter:${imageFilter};
          "
        />

        <span
          style="
            position:absolute;

            right:1px;
            bottom:3px;

            width:13px;
            height:13px;

            background:${statusColor};

            border-radius:50%;

            border:2px solid #ffffff;

            box-shadow:
              0 1px 4px rgba(0,0,0,0.35);
          "
        ></span>
      </div>
    `,

    iconSize: [48, 52],

    iconAnchor: [24, 48],

    popupAnchor: [0, -47],
  });
};

/*
|--------------------------------------------------------------------------
| LEGEND
|--------------------------------------------------------------------------
*/

function LegendControl() {
  const map = useMap();

  useEffect(() => {
    const legend = L.control({
      position: 'bottomleft',
    });

    legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'dashboard-map-legend');

      div.style.background = 'rgba(255,255,255,0.96)';

      div.style.padding = '12px 14px';

      div.style.borderRadius = '10px';

      div.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';

      div.style.fontSize = '12px';

      div.style.maxHeight = '360px';

      div.style.overflowY = 'auto';

      div.style.minWidth = '185px';

      let wilayahHtml = '';

      Object.entries(WILAYAH_CONFIG).forEach(([id, wilayah]) => {
        wilayahHtml += `
            <div
              style="
                display:flex;
                align-items:center;
                gap:7px;
                margin-top:5px;
              "
            >
              <span
                style="
                  display:inline-block;

                  width:14px;
                  height:14px;

                  flex-shrink:0;

                  background:${wilayah.color};

                  border:
                    1px solid rgba(0,0,0,0.10);

                  border-radius:3px;
                "
              ></span>

              <span>
                ${wilayah.name}
              </span>
            </div>
          `;
      });

      div.innerHTML = `
        <div
          style="
            font-weight:700;
            margin-bottom:6px;
          "
        >
          Status Tiket
        </div>

        <div>
          <span style="color:#0d6efd">
            ●
          </span>
          New
        </div>

        <div>
          <span style="color:#f59e0b">
            ●
          </span>
          In Progress
        </div>

        <div>
          <span style="color:#ef4444">
            ●
          </span>
          Pending
        </div>

        <hr
          style="
            margin:8px 0;
          "
        />

        <div
          style="
            font-weight:700;
            margin-bottom:6px;
          "
        >
          Status Petugas
        </div>

        <div>
          <span style="color:#22c55e">
            ●
          </span>
          Aktif
        </div>

        <div>
          <span style="color:#94a3b8">
            ●
          </span>
          Tidak Aktif
        </div>

        <hr
          style="
            margin:8px 0;
          "
        />

        <div
          style="
            font-weight:700;
            margin-bottom:6px;
          "
        >
          Wilayah
        </div>

        ${wilayahHtml}

        <div
          style="
            display:flex;
            align-items:center;
            gap:7px;
            margin-top:5px;
          "
        >
          <span
            style="
              display:inline-block;

              width:14px;
              height:14px;

              background:${DEFAULT_AREA_COLOR};

              border:
                1px solid rgba(0,0,0,0.10);

              border-radius:3px;
            "
          ></span>

          <span>
            Belum terpetakan
          </span>
        </div>
      `;

      L.DomEvent.disableClickPropagation(div);

      L.DomEvent.disableScrollPropagation(div);

      return div;
    };

    legend.addTo(map);

    return () => {
      legend.remove();
    };
  }, [map]);

  return null;
}

/*
|--------------------------------------------------------------------------
| MAIN
|--------------------------------------------------------------------------
*/

const MapTangerang = () => {
  /*
  |--------------------------------------------------------------------------
  | DATA
  |--------------------------------------------------------------------------
  */

  const [points, setPoints] = useState([]);

  const [petugas, setPetugas] = useState([]);

  const [wilayahSummary, setWilayahSummary] = useState([]);

  /*
  |--------------------------------------------------------------------------
  | FILTER
  |--------------------------------------------------------------------------
  */

  const [layerFilter, setLayerFilter] = useState('all');

  const [filterStatus, setFilterStatus] = useState('all');

  const [filterJenis, setFilterJenis] = useState('all');

  const [filterPetugasStatus, setFilterPetugasStatus] = useState('all');

  const [filterPetugasId, setFilterPetugasId] = useState('all');

  const [filterWilayah, setFilterWilayah] = useState('all');

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  const [loading, setLoading] = useState(false);

  const [isFullscreen, setIsFullscreen] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | DEBUG GEOJSON FINAL
  |--------------------------------------------------------------------------
  |
  | Sementara jangan dihapus.
  |
  | Lihat:
  |
  | F12 -> Console
  |
  */

  useEffect(() => {
    const features = Array.isArray(kabupaten?.features) ? kabupaten.features : [];

    const mapped = features.filter((feature) => {
      const wilayahId = feature?.properties?.wilayah_id;

      return wilayahId !== null && wilayahId !== undefined && wilayahId !== '';
    });

    const perWilayah = features.reduce((result, feature) => {
      const wilayahId = feature?.properties?.wilayah_id;

      const key =
        wilayahId === null || wilayahId === undefined || wilayahId === '' ? 'BELUM_MAPPING' : String(wilayahId);

      result[key] = (result[key] || 0) + 1;

      return result;
    }, {});

    console.log('===================================');

    console.log('DEBUG GEOJSON WILAYAH');

    console.log('===================================');

    console.log('Total polygon:', features.length);

    console.log('Polygon sudah mapped:', mapped.length);

    console.log('Polygon belum mapped:', features.length - mapped.length);

    console.log('Polygon per wilayah:', perWilayah);

    console.log('Feature pertama:', features[0]?.properties);

    console.log('Contoh feature mapped:', mapped[0]?.properties);

    console.log('===================================');
  }, []);

  /*
  |--------------------------------------------------------------------------
  | FULLSCREEN
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  /*
  |--------------------------------------------------------------------------
  | LOAD WORK ORDER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchPoints();
  }, [filterStatus]);

  const fetchPoints = async () => {
    try {
      setLoading(true);

      let url = '/work-orders';

      if (filterStatus === 'new') {
        url = '/work-orders/status/new';
      }

      if (filterStatus === 'in_progress') {
        url = '/work-orders/status/in_progress';
      }

      if (filterStatus === 'pending') {
        url = '/work-orders/status/pending';
      }

      const res = await api.get(url);

      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const mapped = raw
        .map((item) => ({
          id: item.id,

          name: item.external_ref,

          lat: Number(item.latitude),

          lng: Number(item.longitude),

          status: String(item.status || '').toLowerCase(),

          jenis: item.category === 'teknical' ? 'teknis' : 'administrasi',

          date: item.created_at,

          wilayah_id: Number(item.wilayah_id ?? item.region_id),

          pelanggan_id: item.pelanggan_id,

          nama_pelanggan: item.nama_pelanggan,

          alamat_pelanggan: item.alamat_pelanggan,

          region_name: item.region_name,

          jenis_pengaduan: item.jenis_pengaduan,

          description: item.description,
        }))
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));

      setPoints(mapped);
    } catch (error) {
      console.error('Work Order map error:', error);
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOAD PETUGAS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchPetugas();

    const interval = setInterval(fetchPetugas, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const fetchPetugas = async () => {
    try {
      const res = await api.get('/users/map-locations');

      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const mapped = raw
        .map((user) => {
          const lat = Number(user.lat ?? user.gps_lat);

          const lng = Number(user.lon ?? user.lng ?? user.gps_long);

          const statusApp = String(user.status_app || 'off').toLowerCase();

          return {
            id: user.id,

            username: user.username || '-',

            lat,

            lng,

            address: user.address ?? user.gps_address ?? '-',

            status_app: statusApp,

            is_active: statusApp === 'on',

            wilayah_id: Number(user.wilayah_id),

            location_updated_at: user.location_updated_at ?? user.gps_updated_at ?? user.updated_at ?? null,
          };
        })
        .filter((user) => Number.isFinite(user.lat) && Number.isFinite(user.lng));

      setPetugas(mapped);
    } catch (error) {
      console.error('Petugas map error:', error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOAD SUMMARY
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchWilayahSummary();
  }, []);

  const fetchWilayahSummary = async () => {
    try {
      const res = await api.get('/dashboard-rekap-wilayah');

      setWilayahSummary(res.data?.data || []);
    } catch (error) {
      console.error('Wilayah summary error:', error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER WORK ORDER
  |--------------------------------------------------------------------------
  */

  const filteredPoints = useMemo(() => {
    if (layerFilter === 'petugas') {
      return [];
    }

    return points.filter((point) => {
      const matchJenis = filterJenis === 'all' || point.jenis === filterJenis;

      const matchWilayah = filterWilayah === 'all' || String(point.wilayah_id) === String(filterWilayah);

      return matchJenis && matchWilayah;
    });
  }, [points, layerFilter, filterJenis, filterWilayah]);

  /*
  |--------------------------------------------------------------------------
  | FILTER PETUGAS
  |--------------------------------------------------------------------------
  */

  const filteredPetugas = useMemo(() => {
    if (layerFilter === 'ticket') {
      return [];
    }

    return petugas.filter((user) => {
      const matchStatus =
        filterPetugasStatus === 'all' ||
        (filterPetugasStatus === 'on' && user.is_active) ||
        (filterPetugasStatus === 'off' && !user.is_active);

      const matchPetugas = filterPetugasId === 'all' || String(user.id) === String(filterPetugasId);

      const matchWilayah = filterWilayah === 'all' || String(user.wilayah_id) === String(filterWilayah);

      return matchStatus && matchPetugas && matchWilayah;
    });
  }, [petugas, layerFilter, filterPetugasStatus, filterPetugasId, filterWilayah]);

  /*
  |--------------------------------------------------------------------------
  | PETUGAS DIPILIH
  |--------------------------------------------------------------------------
  */

  const selectedPetugas = useMemo(() => {
    if (filterPetugasId === 'all') {
      return null;
    }

    return petugas.find((user) => String(user.id) === String(filterPetugasId)) || null;
  }, [petugas, filterPetugasId]);

  /*
  |--------------------------------------------------------------------------
  | COUNT TICKET
  |--------------------------------------------------------------------------
  */

  const ticketCounts = useMemo(() => {
    return filteredPoints.reduce(
      (result, point) => {
        if (point.status === 'new') {
          result.new += 1;
        }

        if (point.status === 'in_progress') {
          result.in_progress += 1;
        }

        if (point.status === 'pending') {
          result.pending += 1;
        }

        return result;
      },
      {
        new: 0,
        in_progress: 0,
        pending: 0,
      },
    );
  }, [filteredPoints]);

  /*
  |--------------------------------------------------------------------------
  | COUNT PETUGAS
  |--------------------------------------------------------------------------
  */

  const petugasCounts = useMemo(() => {
    const dataByWilayah = petugas.filter(
      (user) => filterWilayah === 'all' || String(user.wilayah_id) === String(filterWilayah),
    );

    return {
      total: dataByWilayah.length,

      active: dataByWilayah.filter((user) => user.is_active).length,

      inactive: dataByWilayah.filter((user) => !user.is_active).length,
    };
  }, [petugas, filterWilayah]);

  /*
  |--------------------------------------------------------------------------
  | GET PROPERTY POLYGON
  |--------------------------------------------------------------------------
  */

  const getPolygonName = (feature) => {
    return feature?.properties?.NAMA || '-';
  };

  const getKelurahanName = (feature) => {
    return feature?.properties?.nama_kelurahan || feature?.properties?.NAMOBJ || '-';
  };

  const getKecamatanName = (feature) => {
    return feature?.properties?.nama_kecamatan || feature?.properties?.WADMKC || '-';
  };

  /*
  |--------------------------------------------------------------------------
  | GET WILAYAH POLYGON
  |--------------------------------------------------------------------------
  |
  | Tidak ada lagi lookup:
  |
  | Polygon_1 -> API -> wilayah
  |
  | Sekarang langsung dari GeoJSON hasil build:
  |
  | feature.properties.wilayah_id
  |
  */

  const getFeatureWilayahId = (feature) => {
    const wilayahId = feature?.properties?.wilayah_id;

    if (wilayahId === undefined || wilayahId === null || wilayahId === '') {
      return null;
    }

    const number = Number(wilayahId);

    if (!Number.isFinite(number)) {
      return null;
    }

    return number;
  };

  /*
  |--------------------------------------------------------------------------
  | STYLE POLYGON
  |--------------------------------------------------------------------------
  */

  const kabupatenStyle = (feature) => {
    const wilayahId = getFeatureWilayahId(feature);

    const config = WILAYAH_CONFIG[wilayahId];

    const selected = filterWilayah === 'all' || String(wilayahId) === String(filterWilayah);

    /*
     * Belum punya wilayah pelayanan.
     */
    if (!config) {
      return {
        color: '#ffffff',

        weight: filterWilayah === 'all' ? 0.8 : 0.5,

        fillColor: DEFAULT_AREA_COLOR,

        fillOpacity: filterWilayah === 'all' ? 0.2 : 0.025,

        opacity: filterWilayah === 'all' ? 0.75 : 0.15,
      };
    }

    /*
     * Sudah masuk wilayah pelayanan.
     */
    return {
      /*
       * Batas desa putih.
       */
      color: '#ffffff',

      weight: selected ? 1.25 : 0.65,

      /*
       * Warna wilayah pelayanan.
       */
      fillColor: config.color,

      fillOpacity: selected ? 0.72 : 0.07,

      opacity: selected ? 1 : 0.22,
    };
  };

  /*
  |--------------------------------------------------------------------------
  | POPUP + HOVER POLYGON
  |--------------------------------------------------------------------------
  */

  const onEachKabupaten = (feature, layer) => {
    const polygonName = getPolygonName(feature);

    const kelurahanName = getKelurahanName(feature);

    const kecamatanName = getKecamatanName(feature);

    const wilayahId = getFeatureWilayahId(feature);

    const wilayah = WILAYAH_CONFIG[wilayahId];

    const mappingStatus = feature?.properties?.mapping_status || '-';

    const geometryMatch = feature?.properties?.geometry_match || '-';

    const nameMatch = feature?.properties?.name_match || '-';

    const kdKelurahan = feature?.properties?.kd_kelurahan || '-';

    const kdKecamatan = feature?.properties?.kd_kecamatan || '-';

    /*
     * Hitung ticket di dalam polygon.
     */
    const tickets = points.filter((point) => {
      try {
        const pt = turf.point([point.lng, point.lat]);

        return turf.booleanPointInPolygon(pt, feature);
      } catch {
        return false;
      }
    });

    const newCount = tickets.filter((item) => item.status === 'new').length;

    const progressCount = tickets.filter((item) => item.status === 'in_progress').length;

    const pendingCount = tickets.filter((item) => item.status === 'pending').length;

    layer.bindPopup(`
        <div
          style="
            min-width:260px;
          "
        >
          <div
            style="
              font-size:16px;
              font-weight:700;
              margin-bottom:3px;
            "
          >
            ${kelurahanName}
          </div>

          <div
            style="
              font-size:12px;
              color:#64748b;
              margin-bottom:8px;
            "
          >
            Kecamatan ${kecamatanName}
          </div>

          <div>
            <strong>
              Polygon:
            </strong>

            ${polygonName}
          </div>

          <div>
            <strong>
              Wilayah:
            </strong>

            ${wilayah?.name || 'Belum masuk wilayah pelayanan'}
          </div>

          <div>
            <strong>
              Kode Wilayah:
            </strong>

            ${wilayah?.code || '-'}
          </div>

          <div>
            <strong>
              Kode Kelurahan:
            </strong>

            ${kdKelurahan}
          </div>

          <div>
            <strong>
              Kode Kecamatan:
            </strong>

            ${kdKecamatan}
          </div>

          <hr
            style="
              margin:8px 0;
            "
          />

          <div>
            <strong>
              Mapping Status:
            </strong>

            ${mappingStatus}
          </div>

          <div
            style="
              font-size:11px;
              color:#64748b;
            "
          >
            Geometry:
            ${geometryMatch}
          </div>

          <div
            style="
              font-size:11px;
              color:#64748b;
            "
          >
            Name:
            ${nameMatch}
          </div>

          <hr
            style="
              margin:8px 0;
            "
          />

          <div>
            <strong>
              Total Tiket:
            </strong>

            ${tickets.length}
          </div>

          <div
            style="
              color:#2563eb;
            "
          >
            New:

            <strong>
              ${newCount}
            </strong>
          </div>

          <div
            style="
              color:#d97706;
            "
          >
            In Progress:

            <strong>
              ${progressCount}
            </strong>
          </div>

          <div
            style="
              color:#dc2626;
            "
          >
            Pending:

            <strong>
              ${pendingCount}
            </strong>
          </div>
        </div>
      `);

    layer.on({
      mouseover: (event) => {
        const target = event.target;

        target.setStyle({
          weight: 2.5,

          fillOpacity: wilayah ? 0.9 : 0.4,
        });

        target.bringToFront();
      },

      mouseout: (event) => {
        event.target.setStyle(kabupatenStyle(feature));
      },
    });
  };

  /*
  |--------------------------------------------------------------------------
  | FORMAT TANGGAL
  |--------------------------------------------------------------------------
  */

  const formatLocationTime = (value) => {
    if (!value) {
      return '-';
    }

    const date = dayjs(value);

    if (!date.isValid()) {
      return '-';
    }

    return date.format('DD/MM/YYYY HH:mm:ss');
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER UI
  |--------------------------------------------------------------------------
  */

  const renderFilters = () => (
    <div
      className="
          d-flex
          flex-wrap
          gap-2
          align-items-center
        "
    >
      <select
        className="form-select"
        style={{
          width: '160px',
        }}
        value={layerFilter}
        onChange={(event) => setLayerFilter(event.target.value)}
      >
        <option value="all">Semua Data</option>

        <option value="ticket">Ticket</option>

        <option value="petugas">Petugas</option>
      </select>

      <select
        className="form-select"
        style={{
          width: '205px',
        }}
        value={filterWilayah}
        onChange={(event) => setFilterWilayah(event.target.value)}
      >
        <option value="all">Semua Wilayah</option>

        {Object.entries(WILAYAH_CONFIG).map(([id, wilayah]) => (
          <option key={id} value={id}>
            {wilayah.name}
          </option>
        ))}
      </select>

      {layerFilter !== 'petugas' && (
        <>
          <select
            className="form-select"
            style={{
              width: '170px',
            }}
            value={filterStatus}
            onChange={(event) => setFilterStatus(event.target.value)}
          >
            <option value="all">Semua Status</option>

            <option value="new">New</option>

            <option value="in_progress">In Progress</option>

            <option value="pending">Pending</option>
          </select>

          <select
            className="form-select"
            style={{
              width: '170px',
            }}
            value={filterJenis}
            onChange={(event) => setFilterJenis(event.target.value)}
          >
            <option value="all">Semua Jenis</option>

            <option value="administrasi">Administrasi</option>

            <option value="teknis">Teknis</option>
          </select>
        </>
      )}

      {layerFilter !== 'ticket' && (
        <>
          <select
            className="form-select"
            style={{
              width: '195px',
            }}
            value={filterPetugasStatus}
            onChange={(event) => setFilterPetugasStatus(event.target.value)}
          >
            <option value="all">Semua Status Petugas</option>

            <option value="on">Petugas Aktif</option>

            <option value="off">Petugas Tidak Aktif</option>
          </select>

          <select
            className="form-select"
            style={{
              width: '240px',
            }}
            value={filterPetugasId}
            onChange={(event) => setFilterPetugasId(event.target.value)}
          >
            <option value="all">All Petugas</option>

            {petugas
              .slice()
              .sort((a, b) => a.username.localeCompare(b.username))
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username}

                  {' - '}

                  {user.is_active ? 'Aktif' : 'Tidak Aktif'}
                </option>
              ))}
          </select>
        </>
      )}
    </div>
  );

  /*
  |--------------------------------------------------------------------------
  | RENDER MAP
  |--------------------------------------------------------------------------
  */

  const renderMap = () => (
    <MapContainer
      center={[-6.1783, 106.6319]}
      zoom={10}
      style={{
        width: '100%',

        height: '100%',

        background: '#eef8f4',
      }}
    >
      <ResizeMap isFullscreen={isFullscreen} />

      <FlyToPetugas user={selectedPetugas} />

      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/*
        |--------------------------------------------------------------------------
        | POLYGON WILAYAH
        |--------------------------------------------------------------------------
        */}

      <GeoJSON
        key={`geo-` + `${filterWilayah}-` + `${filterStatus}-` + `${filterJenis}-` + `${points.length}`}
        data={kabupaten}
        style={kabupatenStyle}
        onEachFeature={onEachKabupaten}
      />

      {/*
        |--------------------------------------------------------------------------
        | WORK ORDER
        |--------------------------------------------------------------------------
        */}

      {filteredPoints.map((point) => (
        <Marker
          key={`wo-${point.id}`}
          position={[point.lat, point.lng]}
          icon={createWorkOrderIcon(point.status, point.jenis)}
        >
          <Popup>
            <div
              style={{
                minWidth: '260px',
              }}
            >
              <div
                style={{
                  fontSize: '16px',

                  fontWeight: 700,

                  marginBottom: '6px',
                }}
              >
                {point.name}
              </div>

              <div>
                <strong>Status:</strong> {point.status}
              </div>

              <div>
                <strong>Jenis:</strong> {point.jenis}
              </div>

              <hr />

              <div
                style={{
                  color: '#2563eb',

                  fontWeight: 700,
                }}
              >
                Biodata Pelanggan
              </div>

              <div>
                <strong>ID:</strong> {point.pelanggan_id || '-'}
              </div>

              <div>
                <strong>Nama:</strong> {point.nama_pelanggan || '-'}
              </div>

              <div>
                <strong>Alamat:</strong> {point.alamat_pelanggan || '-'}
              </div>

              <div>
                <strong>Wilayah:</strong> {point.region_name || '-'}
              </div>

              <hr />

              <div
                style={{
                  color: '#dc2626',

                  fontWeight: 700,
                }}
              >
                Pengaduan
              </div>

              <div>
                <strong>Jenis:</strong> {point.jenis_pengaduan || '-'}
              </div>

              <div>
                <strong>Deskripsi:</strong> {point.description || '-'}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}

      {/*
        |--------------------------------------------------------------------------
        | PETUGAS
        |--------------------------------------------------------------------------
        */}

      {filteredPetugas.map((user) => {
        const wilayah = WILAYAH_CONFIG[user.wilayah_id];

        return (
          <Marker
            key={`petugas-${user.id}`}
            position={[user.lat, user.lng]}
            icon={createPetugasIcon(user.is_active)}
            zIndexOffset={1000}
          >
            <Popup>
              <div
                style={{
                  minWidth: '245px',
                }}
              >
                <div
                  style={{
                    fontSize: '16px',

                    fontWeight: 700,

                    marginBottom: '7px',
                  }}
                >
                  {user.username}
                </div>

                <div>
                  Status:{' '}
                  <span
                    style={{
                      color: user.is_active ? '#16a34a' : '#64748b',

                      fontWeight: 700,
                    }}
                  >
                    {user.is_active ? '● Aktif' : '● Tidak Aktif'}
                  </span>
                </div>

                <div
                  style={{
                    marginTop: '6px',
                  }}
                >
                  <strong>Wilayah:</strong> {wilayah?.name || '-'}
                </div>

                <hr
                  style={{
                    margin: '8px 0',
                  }}
                />

                <div>
                  <strong>{user.is_active ? 'Lokasi saat ini' : 'Lokasi terakhir'}</strong>
                </div>

                <div
                  style={{
                    marginTop: '3px',
                  }}
                >
                  {user.address || '-'}
                </div>

                <div
                  style={{
                    marginTop: '7px',

                    color: '#64748b',

                    fontSize: '12px',
                  }}
                >
                  Update lokasi:
                  <br />
                  {formatLocationTime(user.location_updated_at)}
                </div>

                <div
                  style={{
                    marginTop: '7px',

                    color: '#94a3b8',

                    fontSize: '11px',
                  }}
                >
                  Latitude: {user.lat}
                  <br />
                  Longitude: {user.lng}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      <LegendControl />
    </MapContainer>
  );

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div>
      {!isFullscreen && <div className="mb-3">{renderFilters()}</div>}

      {isFullscreen ? (
        <div
          style={{
            position: 'fixed',

            inset: 0,

            zIndex: 999999,

            background: '#ffffff',
          }}
        >
          <div
            style={{
              position: 'absolute',

              top: 15,

              left: 60,

              right: 190,

              zIndex: 9999999,

              background: 'rgba(255,255,255,0.95)',

              padding: '10px',

              borderRadius: '10px',

              boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
            }}
          >
            {renderFilters()}
          </div>

          <button
            type="button"
            className="btn btn-dark"
            style={{
              position: 'absolute',

              top: 15,

              right: 15,

              zIndex: 99999999,
            }}
            onClick={() => setIsFullscreen(false)}
          >
            ✕ Exit
          </button>

          {renderMap()}
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body p-2">
            <div
              className="
                d-flex
                justify-content-between
                align-items-center
                px-2
                mb-2
              "
            >
              <div>
                <strong>Peta Work Order & Petugas</strong>

                <div className="text-muted small">Monitoring lokasi dan tiket</div>
              </div>

              <button type="button" className="btn btn-dark" onClick={() => setIsFullscreen(true)}>
                🔍 Fullscreen
              </button>
            </div>

            <div
              style={{
                height: '650px',
              }}
            >
              {renderMap()}
            </div>
          </div>
        </div>
      )}

      {/*
      |--------------------------------------------------------------------------
      | SUMMARY PETUGAS
      |--------------------------------------------------------------------------
      */}

      {!isFullscreen && layerFilter !== 'ticket' && (
        <div className="row mt-3">
          <div className="col-md-4 mb-2">
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <div className="text-muted">Total Petugas</div>

                <h3 className="mb-0">{petugasCounts.total}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-2">
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <div
                  style={{
                    color: '#16a34a',
                  }}
                >
                  ● Aktif
                </div>

                <h3 className="mb-0">{petugasCounts.active}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-2">
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <div
                  style={{
                    color: '#64748b',
                  }}
                >
                  ● Tidak Aktif
                </div>

                <h3 className="mb-0">{petugasCounts.inactive}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*
      |--------------------------------------------------------------------------
      | SUMMARY TICKET
      |--------------------------------------------------------------------------
      */}

      {!isFullscreen && layerFilter !== 'petugas' && (
        <div className="row mt-3">
          <div className="col-md-4 mb-2">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center">
                <h6 className="text-white">New</h6>

                <h3 className="text-white mb-0">{ticketCounts.new}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-2">
            <div className="card bg-warning h-100">
              <div className="card-body text-center">
                <h6>In Progress</h6>

                <h3 className="mb-0">{ticketCounts.in_progress}</h3>
              </div>
            </div>
          </div>

          <div className="col-md-4 mb-2">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center">
                <h6 className="text-white">Pending</h6>

                <h3 className="text-white mb-0">{ticketCounts.pending}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*
      |--------------------------------------------------------------------------
      | REKAP WILAYAH
      |--------------------------------------------------------------------------
      */}

      {!isFullscreen && (
        <div className="row mt-3">
          {wilayahSummary.map((wilayah) => {
            const config = WILAYAH_CONFIG[Number(wilayah.wilayah_id)];

            return (
              <div
                key={wilayah.wilayah_id}
                className="
                    col-md-4
                    col-lg-3
                    mb-3
                  "
              >
                <div
                  className="card shadow-sm h-100"
                  style={{
                    borderTop: `5px solid ${config?.color || DEFAULT_AREA_COLOR}`,
                  }}
                >
                  <div className="card-body">
                    <h6>
                      <strong>{wilayah.nama_wilayah || config?.name || '-'}</strong>
                    </h6>

                    <hr />

                    <div className="text-primary">
                      New: <strong>{wilayah.total_new}</strong>
                    </div>

                    <div className="text-warning">
                      In Progress: <strong>{wilayah.total_in_progress}</strong>
                    </div>

                    <div className="text-danger">
                      Pending: <strong>{wilayah.total_pending}</strong>
                    </div>

                    <hr />

                    <strong>Total: {wilayah.total_all}</strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && <div className="text-center py-3">Memuat data peta...</div>}
    </div>
  );
};

export default MapTangerang;
